import { Input } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import { nextItemId, withoutEmpty } from "./blockContent";
import { BlockItemList, RichTextField, useUploadTracker } from "./blockEditorParts";
import { withoutEmptySection } from "./landingEditorParts";
import { SectionFields, type SectionFormValues, toSectionFormValues } from "./SectionFields";
import type { BlockContentErrors, StepsContent, StepsItem } from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type StepsEditorProps = {
  value: StepsContent;
  onChange: (value: StepsContent) => void;
  errors: BlockContentErrors;
  onUploadingChange?: (uploading: boolean) => void;
};

type StepsFormItem = StepsItem & { heading: string; image: string; imageAlt: string };

type StepsFormValues = SectionFormValues & {
  intro: string;
  items: StepsFormItem[];
};

function toContent(initial: StepsContent, values: StepsFormValues): StepsContent {
  return withoutEmptySection(
    {
      ...initial,
      ...values,
      items: values.items.map((item, index) =>
        withoutEmpty({ ...item, order: index }, ["heading", "image", "imageAlt"]),
      ),
    },
    ["intro"],
  );
}

export function StepsEditor({ value, onChange, errors, onUploadingChange }: StepsEditorProps) {
  const [initial] = useState(value);
  const { control, register, watch, getValues } = useForm<StepsFormValues>({
    defaultValues: {
      ...toSectionFormValues(value),
      intro: value.intro ?? "",
      items: value.items.map(item => ({
        ...item,
        heading: item.heading ?? "",
        image: item.image ?? "",
        imageAlt: item.imageAlt ?? "",
      })),
    },
  });
  const { fields, append, remove, move } = useFieldArray<StepsFormValues, "items", "fieldKey">({
    control,
    name: "items",
    keyName: "fieldKey",
  });
  const { uploading, report } = useUploadTracker(onUploadingChange);
  useContentChange(watch, onChange, values => toContent(initial, values));

  return (
    <div className={styles.blockEditor}>
      <SectionFields register={register} idPrefix="steps" errors={errors} />

      <RichTextField
        control={control}
        name="intro"
        id="steps-intro"
        label="Intro (optional)"
        minHeight={120}
        emptyAsBlank
      />

      <BlockItemList
        noun="Step"
        keys={fields.map(field => field.fieldKey)}
        disabled={uploading}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => {
          const items = getValues("items");
          append({
            id: nextItemId(items),
            order: items.length,
            heading: "",
            text: "",
            image: "",
            imageAlt: "",
          });
        }}
        renderItem={(index, key) => (
          <>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`steps-heading-${key}`}>
                Heading for step {index + 1} (optional)
              </label>
              <Input id={`steps-heading-${key}`} {...register(`items.${index}.heading`)} />
            </div>
            <RichTextField
              control={control}
              name={`items.${index}.text`}
              id={`steps-text-${key}`}
              label={`Text for step ${index + 1}`}
              minHeight={120}
              emptyAsBlank
            />
            <Controller
              name={`items.${index}.image`}
              control={control}
              render={({ field }) => (
                <CmsImageUploadField
                  value={field.value}
                  onChange={field.onChange}
                  onUploadingChange={pending => report(key, pending)}
                  label="Picture"
                  noun="image"
                  cardLabel={`Step ${index + 1}`}
                  previewAlt={`Step ${index + 1} picture`}
                  guidance="JPG, PNG, GIF or WebP, up to 20 MiB. Shown under the step's text; animated GIFs keep animating."
                />
              )}
            />
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`steps-image-alt-${key}`}>
                Picture description (alt text)
              </label>
              <Input
                id={`steps-image-alt-${key}`}
                error={errors[`items.${index}.imageAlt`]}
                {...register(`items.${index}.imageAlt`)}
              />
            </div>
          </>
        )}
      />
    </div>
  );
}
